import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import query from '../../../constants/queries';
import { GetAllUsers, GetUserProfile, GetUserStatus } from '../../../services/User';
import IntakeDialog from './IntakeDialog';

import { Button } from '../../../lib/button';
import {
    ChartNoAxesColumn,
    CirclePause,
    LayoutGrid,
    Megaphone,
    Rows3,
    SquarePlus,
    Table2,
    UserRoundCheck
} from 'lucide-react';
import sidebarState from '../../../stores/atoms/sidebarAtom';
import { useBoard } from '../../../pages/CaseBoard/useBoard';

const NavigationList = () => {
    const navigate = useNavigate();
    const { data: userStatuses } = useQuery([query.userStatuses], () => GetUserStatus());
    const statuses = userStatuses?.map((status) => status.type);
    const userStatusIsIntake = statuses?.includes('intake');
    const [showIntakeDialog, setShowIntakeDialog] = useState(false);
    const { data: user } = useQuery(['userProfile'], () => GetUserProfile(), {
        retry: false
    });
    const { data: users } = useQuery(['users'], () => GetAllUsers());
    const sidebarOpen = useRecoilValue(sidebarState);
    const { isReadOnly } = useBoard();

    useEffect(() => {
        if (users) {
            users.sort((a, b) => a.full_name.localeCompare(b.full_name));
        }
    }, [users]);

    const showAdditionalOptions = user?.can_finish_intake || user?.can_edit_campaigns || user?.can_change_assignments;

    return (
        <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
                {sidebarOpen && <p className="text-xs font-bold text-grey-400 pl-4">Case</p>}
                <div className="flex flex-col gap-0">
                    <Button
                        variant="ghost"
                        className="flex items-center justify-start gap-3 w-full"
                        onClick={() => navigate('/add-new-case')}
                        disabled={isReadOnly || userStatusIsIntake}
                    >
                        <SquarePlus className="h-4 w-4" />
                        {sidebarOpen && <span className="text-base text-grey-600">Create a case</span>}
                    </Button>
                    <Button
                        variant="ghost"
                        className="flex items-center justify-start gap-3 w-full"
                        onClick={() => navigate('/case-list/assigned-cases')}
                    >
                        <Rows3 className="h-4 w-4" />
                        {sidebarOpen && <span className="text-base text-grey-600">My cases</span>}
                    </Button>
                    <Button
                        variant="ghost"
                        className="flex items-center justify-start gap-3 w-full"
                        onClick={() => navigate('/cases/all')}
                    >
                        <LayoutGrid className="h-4 w-4" />
                        {sidebarOpen && <span className="text-base text-grey-600">All cases</span>}
                    </Button>
                </div>
            </div>
            <div className="flex flex-col gap-4">
                {sidebarOpen && <p className="text-xs font-bold text-grey-400 pl-4">Statistics</p>}
                <div className="flex flex-col gap-0">
                    <Button
                        variant="ghost"
                        className="flex items-center justify-start gap-3 w-full"
                        onClick={() => navigate('/dashboards/all')}
                    >
                        <Table2 className="h-4 w-4" />
                        {sidebarOpen && <span className="text-base text-grey-600">Dashboards</span>}
                    </Button>
                    {user?.can_use_im_tracker && (
                        <Button
                            variant="ghost"
                            className="flex items-center justify-start gap-3 w-full"
                            onClick={() => navigate('/im-tracker')}
                        >
                            <ChartNoAxesColumn className="h-4 w-4" />
                            {sidebarOpen && <span className="text-base text-grey-600">IM Tracker</span>}
                        </Button>
                    )}
                </div>
            </div>
            {showAdditionalOptions && (
                <>
                    <div className="flex flex-col gap-4">
                        {sidebarOpen && <p className="text-xs font-bold text-grey-400 pl-4">Other</p>}
                        <div className="flex flex-col gap-0">
                            {user?.can_finish_intake && (
                                <Button
                                    variant="ghost"
                                    className="flex items-center justify-start gap-3 w-full"
                                    onClick={() => {
                                        setShowIntakeDialog(true);
                                    }}
                                >
                                    <CirclePause className="h-4 w-4" />
                                    {sidebarOpen && <span className="text-base text-grey-600">Finish intake</span>}
                                </Button>
                            )}
                            {user?.can_edit_campaigns && (
                                <Button
                                    variant="ghost"
                                    className="flex items-center justify-start gap-3 w-full"
                                    onClick={() => navigate('/campaigns/all')}
                                >
                                    <Megaphone className="h-4 w-4" />
                                    {sidebarOpen && <span className="text-base text-grey-600">Campaigns</span>}
                                </Button>
                            )}
                            {user?.can_change_assignments && (
                                <Button
                                    variant="ghost"
                                    className="flex items-center justify-start gap-3 w-full"
                                    onClick={() => navigate('/assignments')}
                                >
                                    <UserRoundCheck className="h-4 w-4" />
                                    {sidebarOpen && <span className="text-base text-grey-600">Assignments</span>}
                                </Button>
                            )}
                        </div>
                    </div>
                    {user && users && showIntakeDialog && (
                        <IntakeDialog user={user} users={users} onDialogClose={() => setShowIntakeDialog(false)} />
                    )}
                </>
            )}
        </div>
    );
};

export default NavigationList;
